import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useMudancas } from '@/hooks/useMudancas';
import { useForm } from 'react-hook-form';
import { Form } from '@/components/ui/form';
import MudancaFormFields from './MudancaFormFields';
import MudancaCIsImpactados from './MudancaCIsImpactados';
import { DynamicCategoriaFields } from '@/components/Tickets/DynamicCategoriaFields';
import { MUDANCA_TIPOS, CLASSIFICACAO_RISCO } from '@/types/mudanca';

interface NewMudancaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const NewMudancaDialog = ({ open, onOpenChange }: NewMudancaDialogProps) => {
  const [formData, setFormData] = useState({
    titulo: '',
    descricao: '',
    urgencia: 'media',
    impacto: 'medio',
    prioridade: 'media',
    tipo_mudanca: MUDANCA_TIPOS.NORMAL,
    classificacao_risco: CLASSIFICACAO_RISCO.MEDIO,
    justificativa_tecnica: '',
    justificativa_negocio: '',
    responsavel_tecnico_id: '',
    aprovador_id: '',
    data_execucao_planejada: '',
    plano_implementacao: '',
    plano_testes: '',
    plano_rollback: '',
    impacto_estimado: '',
    riscos_identificados: '',
    cis_impactados: [],
    categoria_id: '',
  });

  const form = useForm({
    defaultValues: {
      campos_customizados: {},
    },
  });

  const { createMudanca } = useMudancas();

  const handleUpdateCIs = (cis: any) => {
    setFormData({ ...formData, cis_impactados: cis });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validar campos obrigatórios
    const requiredFields = [
      'titulo', 'descricao', 'justificativa_tecnica', 'justificativa_negocio',
      'responsavel_tecnico_id', 'data_execucao_planejada', 'plano_implementacao',
      'plano_testes', 'plano_rollback', 'impacto_estimado'
    ];
    
    const missingFields = requiredFields.filter(field => !formData[field as keyof typeof formData]);
    
    if (missingFields.length > 0) {
      alert(`Campos obrigatórios não preenchidos: ${missingFields.join(', ')}`);
      return;
    }

    createMudanca.mutate({
      ...formData,
    }, {
      onSuccess: () => {
        onOpenChange(false);
        setFormData({
          titulo: '',
          descricao: '',
          urgencia: 'media',
          impacto: 'medio',
          prioridade: 'media',
          tipo_mudanca: MUDANCA_TIPOS.NORMAL,
          classificacao_risco: CLASSIFICACAO_RISCO.MEDIO,
          justificativa_tecnica: '',
          justificativa_negocio: '',
          responsavel_tecnico_id: '',
          aprovador_id: '',
          data_execucao_planejada: '',
          plano_implementacao: '',
          plano_testes: '',
          plano_rollback: '',
          impacto_estimado: '',
          riscos_identificados: '',
          cis_impactados: [],
          categoria_id: '',
        });
        form.reset({
          campos_customizados: {},
        });
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Nova Solicitação de Mudança</DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="h-[calc(90vh-8rem)] pr-6">
          <Tabs defaultValue="basico" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="basico">Informações Básicas</TabsTrigger>
              <TabsTrigger value="cis">CIs Impactados</TabsTrigger>
              <TabsTrigger value="campos">Campos Customizados</TabsTrigger>
            </TabsList>

            <TabsContent value="basico" className="space-y-6 mt-6">
              <MudancaFormFields 
                formData={formData}
                setFormData={setFormData}
                isEdit={false}
              />
            </TabsContent>

            <TabsContent value="cis" className="space-y-6 mt-6">
              <MudancaCIsImpactados
                cisImpactados={formData.cis_impactados}
                onUpdate={handleUpdateCIs}
              />
            </TabsContent>

            <TabsContent value="campos" className="space-y-6 mt-6">
              <div className="border rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4">Campos Customizados da Categoria</h3>
                <Form {...form}>
                  <DynamicCategoriaFields 
                    form={form} 
                    categoriaId={formData.categoria_id} 
                  />
                  {!formData.categoria_id && (
                    <p className="text-sm text-muted-foreground">
                      Selecione uma categoria na aba de Informações Básicas
                    </p>
                  )}
                </Form>
              </div>
            </TabsContent>
          </Tabs>

          <form onSubmit={handleSubmit}>
            <div className="flex justify-end space-x-2 pt-6 border-t mt-6">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={createMudanca.isPending}>
                {createMudanca.isPending ? 'Criando...' : 'Criar Solicitação de Mudança'}
              </Button>
            </div>
          </form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default NewMudancaDialog;